import {t, Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  buildCreateContactFormBoxStyles,
  computeIsCreateContactFormFieldErred,
} from '../../../../../../../../src/helpers/createContactFormHelpers';
import {CreateContactPhoneFormFieldModel} from '../../../../../../../../src/store/panels/contacts/createContactForm/createContactFormState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Localizer} from '../../../../../../../../src/store/stateless/internationalizationSelectors';
import {useDebounce} from '../../../../../../../../srcCommon/helpers/hookHelpers';
import {useLocalize} from '../../../../../../../../srcCommon/internationalization/internationalizationContext';
import {Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInputIcon';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {CreateContactInformationFieldActions} from './createContactInformationFieldActions';
import {CreateContactInformationFormFieldErrorContainer} from './createContactInformationFormFieldErrorContainer';

/*
 * Constants.
 */

const inputWidth = 184; // px.
const debounceDelay = 500; // ms.

/*
 * Props.
 */

interface CreateContactInformationPhoneInputProps {
  panelId: ContactsManagerPanelId;
  phoneNumber: CreateContactPhoneFormFieldModel;
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;

  onUpdatePhoneNumberAsync: UpdatePhoneNumberAsyncHandler;
  onAddPhone: Handler;
  onRemovePhone: HandlerOf<string>;
}

type UpdatePhoneNumberAsyncHandler = (uid: string, value: string) => Promise<void>;

/*
 * Style.
 */

const StyledPhoneInputWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledBoxedInput = styled(BoxedInput)`
  flex: 1;
`;

const StyledBoxedInputElement = styled(BoxedInputElement)`
  margin-left: 1px;
`;

/*
 * Component.
 */

export const CreateContactInformationPhoneInput: FC<CreateContactInformationPhoneInputProps> = (props) => {
  const {
    panelId,
    phoneNumber,
    isFirst,
    isLast,
    isDisabled,
    onUpdatePhoneNumberAsync,
    onAddPhone,
    onRemovePhone,
  } = props;
  const localize = useLocalize();

  const onPhoneNumberUpdatedDebounced = useDebounce(
    (value: string) => onUpdatePhoneNumberAsync(phoneNumber.uid, value.trim()),
    debounceDelay,
  );
  const onInputBlur = () => onPhoneNumberUpdatedDebounced.flush();

  return (
    <StyledPhoneInputWrapperDiv>
      {renderInput(panelId, phoneNumber, isDisabled, localize, onPhoneNumberUpdatedDebounced, onInputBlur)}
      {!isDisabled && (
        <CreateContactInformationFieldActions
          hasValue={Boolean(phoneNumber.value)}
          isFirst={isFirst}
          isLast={isLast}
          addAnotherLabel={
            <Trans id="create-contact-form-phone-add-phone-button">Add another phone number</Trans>
          }
          onAdd={onAddPhone}
          onRemove={() => onRemovePhone(phoneNumber.uid)}
        />
      )}
    </StyledPhoneInputWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderInput(
  panelId: ContactsManagerPanelId,
  phoneNumber: CreateContactPhoneFormFieldModel,
  isDisabled: boolean,
  localize: Localizer,
  onPhoneNumberUpdatedDebounced: HandlerOf<string>,
  onInputBlur: Handler,
) {
  const hasError = computeIsCreateContactFormFieldErred(Boolean(phoneNumber.value), phoneNumber.validity);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledBoxedInput
          hasError={hasError}
          isDisabled={isDisabled}
          errorMessage={
            <CreateContactInformationFormFieldErrorContainer
              panelId={panelId}
              formField={phoneNumber}
              formFieldName={<Trans id="create-contact-form-field-phone-name">Phone number</Trans>}
            />
          }
          boxStyles={buildCreateContactFormBoxStyles(false, hasError, inputWidth)}
        >
          {(childProps) => (
            <>
              <BoxedInputIcon
                {...childProps}
                iconName="phoneSmall"
                color={theme.greys.shade70}
                size={VisualSizesEnum.MEDIUM}
              />
              <StyledBoxedInputElement
                {...childProps}
                type={BoxedInputElementTypesEnum.TEXT}
                placeholder={localize(t('create-contact-form-phone-placeholder')`Add phone number`)}
                onChange={onPhoneNumberUpdatedDebounced}
                onBlur={onInputBlur}
                size={VisualSizesEnum.MEDIUM}
                defaultValue={phoneNumber.value}
              />
            </>
          )}
        </StyledBoxedInput>
      )}
    </UserThemeConsumer>
  );
}
